<template>
  <div class="google-auth-button">
    <div v-if="isConnected" class="oauth-status">
      <span class="connected-status">✓ {{ $t('auth.google_connected') }}</span>
      <Btn
        :text="$t('auth.google_disconnect')"
        variant="danger"
        size="sm"
        @click="handleDisconnect"
      />
    </div>
    <Btn
      v-else
      :id="buttonId"
      :text="buttonText"
      icon="google"
      @click="handleConnect"
      variant="tertiary"
      size="md"
      :loading="loading"
      class="width-100"
    />
  </div>
</template>

<script>
  import { ref, computed, getCurrentInstance } from 'vue';
  import { supabase } from '@/services/supabase';
  import IntegrationService from '@/services/integrationService';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'GoogleAuthButton',

    components: {
      Btn,
    },

    props: {
      mode: {
        type: String,
        required: true,
        validator: (value) => ['calendar', 'auth'].includes(value),
      },
      buttonId: {
        type: String,
        default: 'google-auth-button',
      },
      isConnected: {
        type: Boolean,
        default: false,
      },
      // Name given to the integration created by the OAuth callback
      name: {
        type: String,
        default: '',
      },
      // When false, the page stays alive and `connected` is emitted once the popup closes
      reloadOnClose: {
        type: Boolean,
        default: true,
      },
    },

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const loading = ref(false);

      const buttonText = computed(() => {
        if (props.mode === 'calendar') {
          return $t('auth.google_connect_calendar');
        }
        return $t('auth.google_sign_in');
      });

      const handleConnect = async () => {
        loading.value = true;
        try {
          if (props.mode === 'calendar') {
            // Get the OAuth URL from backend
            const response = await IntegrationService.connectGoogleCalendar(props.name);
            const { url } = response;

            const authWindow = window.open(url, 'google-calendar-auth', 'width=600,height=600');

            if (authWindow) {
              const checkWindow = setInterval(() => {
                if (authWindow.closed) {
                  clearInterval(checkWindow);
                  if (props.reloadOnClose) {
                    window.location.reload();
                  } else {
                    emit('connected', {});
                  }
                }
              }, 500);
            }
          } else {
            // Regular auth remains unchanged
            const { data, error } = await supabase.auth.signInWithOAuth({
              provider: 'google',
              options: {
                redirectTo: `${window.location.origin}/oauth/google-callback`,
                queryParams: {
                  access_type: 'offline',
                  prompt: 'consent',
                },
              },
            });
            if (error) throw error;
            emit('connected', data);
          }
        } catch (error) {
          console.error('OAuth error:', error);
          emit('error', error);
        } finally {
          loading.value = false;
        }
      };

      const handleDisconnect = async () => {
        loading.value = true;
        try {
          if (props.mode === 'calendar') {
            // Revoke calendar access
            emit('disconnect');
          } else {
            // Sign out from Supabase
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
            emit('disconnect');
          }
        } catch (error) {
          console.error('Disconnect error:', error);
          emit('error', error);
        } finally {
          loading.value = false;
        }
      };

      return {
        loading,
        buttonText,
        handleConnect,
        handleDisconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .google-auth-button {
    .oauth-status {
      display: flex;
      align-items: center;
      gap: 1rem;

      .connected-status {
        color: var(--green-600);
        font-weight: 500;
      }
    }
  }
</style>

<!-- For authentication -->
<!-- <GoogleAuthButton 
  mode="auth"
  @connected="handleAuthSuccess"
  @error="handleAuthError"
/>

<GoogleAuthButton 
  mode="calendar"
  :is-connected="hasCalendarAccess"
  @connected="handleCalendarConnected"
  @disconnect="handleCalendarDisconnect"
  @error="handleError"
/> -->
